import { useState, useRef } from "react";
import { Sparkles, Send, Loader2, Square } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import ReactMarkdown from "react-markdown";

const AI_SEARCH_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-search`;

const SUGGESTED_QUESTIONS = [
  "Quem falta confirmar hoje?",
  "Quantos pacientes atendi esta semana?",
  "Quais pacientes sem retorno há 30 dias?",
  "Resumo da agenda de amanhã",
];

export function AiSearchPanel() {
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const handleAsk = async (overrideQuestion?: string) => {
    const q = (overrideQuestion ?? question).trim();
    if (!q || q.length < 3 || isStreaming) return;

    if (overrideQuestion) setQuestion(overrideQuestion);
    setAnswer("");
    setError(null);
    setIsStreaming(true);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      if (!accessToken) {
        setError("Sessão expirada. Faça login novamente.");
        setIsStreaming(false);
        return;
      }

      const resp = await fetch(AI_SEARCH_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ question: q }),
        signal: controller.signal,
      });

      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        setError(body.error || `Erro ${resp.status}`);
        setIsStreaming(false);
        return;
      }

      if (!resp.body) {
        setError("Sem resposta do servidor");
        setIsStreaming(false);
        return;
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let textBuffer = "";
      let accumulated = "";
      let streamDone = false;

      while (!streamDone) {
        const { done, value } = await reader.read();
        if (done) break;
        textBuffer += decoder.decode(value, { stream: true });

        let newlineIndex: number;
        while ((newlineIndex = textBuffer.indexOf("\n")) !== -1) {
          let line = textBuffer.slice(0, newlineIndex);
          textBuffer = textBuffer.slice(newlineIndex + 1);

          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (line.startsWith(":") || line.trim() === "") continue;
          if (!line.startsWith("data: ")) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === "[DONE]") {
            streamDone = true;
            break;
          }

          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content as string | undefined;
            if (content) {
              accumulated += content;
              setAnswer(accumulated);
            }
          } catch {
            textBuffer = line + "\n" + textBuffer;
            break;
          }
        }
      }

      // Flush remaining
      if (textBuffer.trim()) {
        for (let raw of textBuffer.split("\n")) {
          if (!raw) continue;
          if (raw.endsWith("\r")) raw = raw.slice(0, -1);
          if (raw.startsWith(":") || raw.trim() === "") continue;
          if (!raw.startsWith("data: ")) continue;
          const jsonStr = raw.slice(6).trim();
          if (jsonStr === "[DONE]") continue;
          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content as string | undefined;
            if (content) {
              accumulated += content;
              setAnswer(accumulated);
            }
          } catch {
            /* ignore */
          }
        }
      }
    } catch (e: any) {
      if (e.name !== "AbortError") {
        setError("Erro ao consultar a IA. Tente novamente.");
        console.error("AI search error:", e);
      }
    } finally {
      setIsStreaming(false);
      abortRef.current = null;
    }
  };

  const handleCancel = () => {
    abortRef.current?.abort();
    setIsStreaming(false);
  };

  return (
    <div className="flex flex-col" style={{ minHeight: 280 }}>
      {/* Input area */}
      <div className="flex items-center gap-2 border-b px-3 py-2">
        <Sparkles className="h-4 w-4 shrink-0 text-primary" />
        <Input
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleAsk()}
          placeholder="Pergunte algo sobre a clínica..."
          className="h-9 border-0 bg-transparent px-0 shadow-none focus-visible:ring-0"
          disabled={isStreaming}
          autoFocus
        />
        {isStreaming ? (
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8 shrink-0"
            onClick={handleCancel}
            title="Parar resposta"
          >
            <Square className="h-4 w-4 fill-current" />
          </Button>
        ) : (
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8 shrink-0"
            onClick={() => handleAsk()}
            disabled={question.trim().length < 3}
          >
            <Send className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Response area */}
      <ScrollArea className="flex-1 px-4 py-3" style={{ maxHeight: 320 }}>
        {error && (
          <p className="text-sm text-destructive">{error}</p>
        )}

        {!answer && !error && !isStreaming && (
          <div className="flex flex-col items-center justify-center gap-3 py-6 text-muted-foreground">
            <Sparkles className="h-8 w-8 opacity-40" />
            <p className="text-sm text-center">
              Pergunte sobre pacientes, agendamentos,<br />serviços ou profissionais da clínica.
            </p>
            {/* Suggested questions (P1.9) */}
            <div className="flex flex-wrap justify-center gap-1.5 mt-2">
              {SUGGESTED_QUESTIONS.map((sq) => (
                <button
                  key={sq}
                  type="button"
                  onClick={() => handleAsk(sq)}
                  className="rounded-full border bg-muted/50 px-3 py-1 text-xs text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
                >
                  {sq}
                </button>
              ))}
            </div>
          </div>
        )}

        {(answer || isStreaming) && (
          <div className="prose prose-sm max-w-none dark:prose-invert text-sm leading-relaxed">
            <ReactMarkdown>{answer || "..."}</ReactMarkdown>
            {isStreaming && (
              <span className="inline-block h-4 w-1 animate-pulse bg-primary ml-0.5" />
            )}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}
